import { Button } from './core';
import { Modal, type Column } from './feedback';
import { invoiceNumber, longDate, money, type InvoicePayment } from '../lib/invoice';
import { useBrandingStore } from '../store/branding';

/**
 * The invoice paper itself — white sheet regardless of theme so it reads (and
 * prints) like a real document. Only the fields both payment rows carry; the
 * admin row adds `billedTo` via props rather than a second template.
 */
export function InvoiceView(props: { r: InvoicePayment; billedTo: string }) {
  const { platformName, supportEmail, footerText } = useBrandingStore();
  const r = props.r;
  const issued = r.paidAt ?? r.createdAt;
  const cycle = r.billingCycle ? r.billingCycle.toLowerCase() : '—';
  const description = r.planName ?? 'Subscription payment';

  return (
    <div className="invoice">
      <div className="invoice-head">
        <div className="invoice-brand">
          <div className="invoice-logo" aria-hidden>{platformName.slice(0, 1).toUpperCase()}</div>
          <div>
            <div className="invoice-name">{platformName}</div>
            <div className="invoice-sub">{footerText ?? 'Stream once, broadcast everywhere'}</div>
          </div>
        </div>
        <div className="invoice-head-right">
          <div className="invoice-title">INVOICE</div>
          <div className="invoice-no">{invoiceNumber(r)}</div>
          <div className="invoice-date">Issued {longDate(issued)}</div>
        </div>
      </div>

      <div className="invoice-parties">
        <div>
          <span className="invoice-label">Billed to</span>
          <strong>{props.billedTo}</strong>
        </div>
        <div>
          <span className="invoice-label">Payment method</span>
          <strong>{(r.method ?? r.provider).replace('_', ' ')}</strong>
        </div>
        <div>
          <span className="invoice-label">Reference</span>
          <strong>{r.providerRef ?? '—'}</strong>
        </div>
        <div>
          <span className="invoice-label">Receipt</span>
          <strong>{r.receipt ?? '—'}</strong>
        </div>
      </div>

      <table className="invoice-table">
        <thead>
          <tr>
            <th>Description</th>
            <th>Billing cycle</th>
            <th>Stream</th>
            <th className="right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className="font-medium">{description}</div>
              <div className="invoice-table-sub">
                {r.provider} · paid {longDate(r.paidAt)}
              </div>
            </td>
            <td>{cycle}</td>
            <td>{r.streamName ?? '—'}</td>
            <td className="right font-medium">{money(r.amount, r.currency)}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>Total ({r.currency})</td>
            <td className="right invoice-total">{money(r.amount, r.currency)}</td>
          </tr>
        </tfoot>
      </table>

      <div className="invoice-foot">
        <span className={`invoice-stamp ${r.status.toLowerCase()}`}>{r.status}</span>
        <div className="invoice-foot-text">
          {r.note ? <p className="invoice-note">“{r.note}”</p> : null}
          <p>Thank you for your business.</p>
          <p className="invoice-muted">
            {supportEmail ? `${supportEmail} · ` : ''}
            Generated {longDate(r.createdAt)} · {platformName}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Modal wrapper: view the invoice, then download it via the browser's print → Save as PDF. */
export function InvoiceModal(props: {
  r: InvoicePayment;
  billedTo: string;
  onClose: () => void;
}) {
  return (
    <Modal
      title={`Invoice ${invoiceNumber(props.r)}`}
      className="wide"
      onClose={props.onClose}
      footer={
        <>
          <span className="text-muted text-sm">Download opens your print dialog — choose “Save as PDF”.</span>
          <Button onClick={() => window.print()}>⤓ Download PDF</Button>
        </>
      }
    >
      <InvoiceView r={props.r} billedTo={props.billedTo} />
    </Modal>
  );
}

/** Shared “Invoice” table column. The span swallows the click so an admin row's
 * navigate-on-click doesn't fire when opening the invoice. */
export function invoiceColumn<T extends InvoicePayment>(onView: (row: T) => void): Column<T> {
  return {
    key: 'invoice',
    header: 'Invoice',
    render: (r) => (
      <span onClick={(e) => e.stopPropagation()}>
        <Button variant="secondary" size="sm" onClick={() => onView(r)} title="View & download invoice">
          View ⤓
        </Button>
      </span>
    ),
  };
}
